import React, {createRef} from 'react';
import PropTypes from 'prop-types';
import PaymentIdentification from '../../PaymentSteps/PaymentIdentification/PaymentIdentification';
import PaymentOptionSelection from '../../PaymentSteps/PaymentOptionSelection/PaymentOptionSelection';
import PaymentRegister from '../../PaymentSteps/PaymentRegister/PaymentRegister';
import PaymentMessage from '../../PaymentSteps/PaymentMessage/PaymentMessage';
import Budget from '../../PaymentDocuments/Budget/Budget';
import DataLoader from '../../../DataLoader/DataLoader';
import DataError from '../../../DataError/DataError';
import Pin from '../../../../helpers/Pin';
import Search from '../../../../helpers/Search';
import Image from '../../../../helpers/Image';
import WebServices from '../../../../helpers/WebServices';
import Activity from '../../../../helpers/Activity';
import Printer from '../../../../helpers/Printer';
import Debug from "../../../../helpers/Debug";
import defaultPersonOptionImage from './images/person-option.png';
import defaultCompanyOptionImage from './images/company-option.png';
import defaultPrintMessageImage from './images/print-message.png';
import './Signup.css';

export default class Signup extends React.Component {
    constructor(props) {
        super(props);
        const entity   = this.props.initialData && this.props.initialData.entity   ? this.props.initialData.entity   : undefined;
        const pin      = this.props.initialData && this.props.initialData.pin      ? this.props.initialData.pin      : undefined;
        const name     = this.props.initialData && this.props.initialData.name     ? this.props.initialData.name     : undefined;
        const phone    = this.props.initialData && this.props.initialData.phone    ? this.props.initialData.phone    : undefined;
        const email    = this.props.initialData && this.props.initialData.email    ? this.props.initialData.email    : undefined;
        const activity = this.props.initialData && this.props.initialData.activity ? this.props.initialData.activity : undefined;
        const state    = this.props.initialData && this.props.initialData.state    ? this.props.initialData.state    : undefined;
        const city     = this.props.initialData && this.props.initialData.city     ? this.props.initialData.city     : undefined;
        const district = this.props.initialData && this.props.initialData.district ? this.props.initialData.district : undefined;
        const street   = this.props.initialData && this.props.initialData.street   ? this.props.initialData.street   : undefined;
        this.state = {
            step: 'identification',
            entity: entity,
            allowEntityChange: true,
            isLoadingEntity: false,
            retries: 0,
            maxRetries: 3,
            pin: pin,
            name: name,
            allowNameChange: true,
            phone: phone,
            email: email,
            activities: [],
            allActivities: [],
            activity: activity,
            isLoadingActivities: true,
            activitiesLoaded: false,
            states: [],
            state: state,
            isLoadingStates: true,
            statesLoaded: false,
            cities: [],
            city: city,
            isLoadingCities: true,
            citiesLoaded: false,
            districts: [],
            district: district,
            isLoadingDistricts: true,
            districtsLoaded: false,
            street: street,
            isLoadingHQData: true,
            hqDataLoaded: false,
            companyName: undefined,
            hqAddress: undefined,
            hqActivity: undefined,
            segments: [],
            segment: undefined,
            isLoadingSegments: true,
            segmentsLoaded: false,
            isSavingCustomer: false,
            saveCustomerError: false,
            isCreatingBudget: false,
            budgetCreated: false,
            budgetLogo: undefined,
            isLoadingBudgetLogo: true,
            budgetPrinted: false,
            budgetPrintError: false

        };
        this._isMounted = true;
        this._containerRef = createRef();
        this._budgetRef = createRef();
    };

    changeState = (newState, callback) => {
        if (this._isMounted) {
            this.setState(newState, () => {
                if (callback) callback();
            });
        }
    };

    componentDidMount() {
        this._isMounted = true;
        this.getActivities();
        this.getStates();
        this.getDistricts();
        this.getCities();
        this.getHQData();
        this.getSegments();
        this.getImageData();
        if (this.state.pin) this.onPreloadFinished(this.state.pin, false);
    };

    componentDidUpdate(prevProps, prevState, snapshot) {
        const finishSteps = ['budget-preview'];
        if (prevState.step !== this.state.step && finishSteps.includes(this.state.step)) {
            this.props.flowFinished();
        }
    };

    componentWillUnmount() {
        this._isMounted = false;
    };

    getActivities = () => {
        WebServices.getActivities()
            .then((response) => {
                this.changeState({allActivities: response, isLoadingActivities: false, activitiesLoaded: !!response});
            });
    };

    getStates = () => {
        WebServices.getStates()
            .then((response) => {
                this.changeState({states: response, isLoadingStates: false, statesLoaded: !!response});
            });
    };

    getDistricts = () => {
        WebServices.getDistricts()
            .then((response) => {
                this.changeState({districts: response, isLoadingDistricts: false, districtsLoaded: !!response});
            });
    };

    getCities = () => {
        WebServices.getCities()
            .then((response) => {
                this.changeState({cities: response, isLoadingCities: false, citiesLoaded: !!response});
            });
    };

    getHQData = () => {
        WebServices.getHQData()
            .then((response) => {
                this.changeState({
                    companyName: response ? response['companyName'] : undefined,
                    hqAddress: response ? response['hqAddress'] : undefined,
                    hqActivity: response ? response['hqActivity'] : undefined,
                    isLoadingHQData: false,
                    hqDataLoaded: !!response
                });
            });
    };

    getSegments = () => {
        WebServices.getSegments()
            .then((response) => {
                this.changeState({segments: response, isLoadingSegments: false, segmentsLoaded: !!response});
            })
    };

    getImageData = () => {
        const {mainClientLogo} = this.props;
        if (mainClientLogo) {
            Image.getBase64(mainClientLogo)
                .then((base64) => {
                    if (base64) {
                        this.changeState({budgetLogo: base64, isLoadingBudgetLogo: false});
                    } else {
                        this.changeState({budgetLogo: undefined, isLoadingBudgetLogo: false});
                    }
                })
                .catch((error) => {
                    // A logo that fails to load must not leave the form loading forever: the budget prints without it.
                    Debug.printToLog('error', error);
                    this.changeState({budgetLogo: undefined, isLoadingBudgetLogo: false});
                });
        } else this.changeState({budgetLogo: undefined, isLoadingBudgetLogo: false});
    };

    isLoadingInitialData = () => {
        const {isLoadingActivities, isLoadingStates, isLoadingDistricts, isLoadingCities, isLoadingHQData, isLoadingSegments, isLoadingBudgetLogo, isLoadingEntity} = this.state;
        const isLoading = isLoadingActivities || isLoadingStates || isLoadingDistricts || isLoadingCities || isLoadingHQData || isLoadingSegments || isLoadingBudgetLogo;
        if (this.state.pin) return isLoading || isLoadingEntity;
        else return isLoading;
    };

    initialDataLoaded = () => {
        const {activitiesLoaded, statesLoaded, districtsLoaded, citiesLoaded, hqDataLoaded, segmentsLoaded} = this.state;
        return activitiesLoaded && statesLoaded && districtsLoaded && citiesLoaded && hqDataLoaded && segmentsLoaded; // Do not include budgetLogo or entity load because they are not required fields, the process can be completed event if this two fields are not present.
    };

    onPreloadFinished = () => {
        this.changeState({isLoadingEntity: true}, this.getClientEntityData);
    };

    onIdentificationFinished = (pin) => {
        Activity.log(this.props.metadata, 'checkout', this.state.step, 'finish', true, {type: this.props.flow, shoppingCart: this.props.shoppingCart.toActivityLog()}); // Do not save user personal data.
        this.changeState({pin: pin, isLoadingEntity: true}, this.getClientEntityData);
    };

    getClientEntityData = () => {
        WebServices.getClientEntity(this.state.pin)
            .then((response) => {
                if (response) {
                    const {states, districts, cities} = this.state;
                    const stateName = response['address']['state'];
                    const districtName = response['address']['district'];
                    const cityName = response['address']['city'];
                    const stateMatch = states.find((state) => Search.normalizeText(state['stateName']) === Search.normalizeText(stateName));
                    const districtMatch = districts.find((district) => Search.normalizeText(district['districtName']) === Search.normalizeText(districtName));
                    const cityMatch = cities.find((city) => Search.normalizeText(city['cityName']) === Search.normalizeText(cityName));
                    const stateValue = stateMatch ? stateMatch['stateId'] : undefined;
                    const districtValue = districtMatch ? districtMatch['districtName'] : undefined;
                    const cityValue = cityMatch ? cityMatch['cityKey'] : undefined;
                    this.changeState({
                        allowEntityChange: false,
                        entity: response['entity'] || undefined,
                        name: response['name'] || this.state.name,
                        phone: response['phone'] || this.state.phone,
                        email: response['email'] || this.state.email,
                        state: stateValue || this.state.state,
                        district: districtValue || this.state.district,
                        city: cityValue || this.state.city,
                        street: response['address']['street'] || this.state.street,
                        segment: response['segment'] || undefined,
                        activity: response['activity'] || undefined
                    }, this.getSiiEntityData);
                } else this.getSiiEntityData();
            });
    };

    getSiiEntityData = () => {
        const {entity, retries, maxRetries} = this.state;
        if (retries < maxRetries) {
            WebServices.getSiiEntity(this.state.pin)
                .then((response) => {
                    if (response) {
                        const name = !this.state.name ? response.name : this.state.name;
                        const activities = this.filterValidActivities(response.activities);
                        const activity = activities.length === 1 ? activities[0]["activityId"] : (this.state.activity || undefined);
                        this.changeState({step: entity ? 'client-register' : 'client-entity-selection', isLoadingEntity: false, name: name, allowNameChange: !name, activities: activities, activity: activity});
                    } else this.changeState({retries: retries + 1}, this.getSiiEntityData);
                });
        } else this.changeState({step: entity ? 'client-register' : 'client-entity-selection', isLoadingEntity: false});
    };

    filterValidActivities = (activities) => {
        const {allActivities} = this.state;
        let validActivities = [];
        activities.forEach((activity) => {
            const activityMatch = allActivities.find((a) => Search.normalizeText(a['activityName']) === Search.normalizeText(activity));
            if (activityMatch) validActivities.push(activityMatch);
        });
        return validActivities;
    };

    onEntitySelected = (entity) => {
        Activity.log(this.props.metadata, 'checkout', this.state.step, `${entity.toLowerCase()}-selected`, true, {type: this.props.flow, entity: entity, shoppingCart: this.props.shoppingCart.toActivityLog()});
        this.changeState({entity: entity, step: 'client-register'});
    };

    getActivityName = (id) => {
        const match = this.state.allActivities.find((activity) => activity['activityId'] === id);
        return match ? match['activityName'] : undefined;
    };

    getCityName = (id) => {
        const match = this.state.cities.find((city) => city['cityKey'] === id);
        return match ? match['cityName'] : undefined;
    };

    getStateName = (id) => {
        const match = this.state.states.find((state) => state['stateId'] === id);
        return match ? match['stateName'] : undefined;
    };

    handlePinChange = () => {
        return null;
    };

    handleActivityChange = (activity) => {
        this.changeState({activity: activity});
    };

    handleNameChange = (data) => {
        this.changeState({name: data.value.toUpperCase()});
    };

    handleAddressChange = (street, district, city, state) => {
        this.changeState({street: street, district: district, city: city, state: state});
    };

    handleStateChange = (state) => {
        this.changeState({state: state, city: undefined, district: undefined});
    };

    handleDistrictChange = (district) => {
        this.changeState({district: district});
    };

    handleCityChange = (city) => {
        this.changeState({city: city});
    };

    handlePhoneChange = (data) => {
        this.changeState({phone: data.value});
    };

    handleEmailChange = (data) => {
        this.changeState({email: data.value});
    };

    handleSegmentChange = (segment) => {
        this.changeState({segment: segment});
    };

    onRegisterFinished = () => {
        Activity.log(this.props.metadata, 'checkout', this.state.step, 'finish', true, {type: this.props.flow, entity: this.state.entity, shoppingCart: this.props.shoppingCart.toActivityLog()});
        this.changeState({isSavingCustomer: true, saveCustomerError: false}, () => {
            const document = this.state.entity === "company" ? "invoice" : "ticket";
            WebServices.saveCustomer({
                entity: this.state.entity,
                document: document,
                segment: this.state.segment,
                pin: this.state.pin,
                name: this.state.name,
                phone: this.state.phone,
                email: this.state.email,
                activity: this.state.activity,
                state: this.state.state,
                city: this.state.city,
                district: this.state.district,
                street: this.state.street
            })
                .then((response) => {
                    if (response && response.status) {
                        if (this.props.flow === 'budget-print') {
                            return WebServices.saveOrder({
                                id: this.props.shoppingCart.dbId,
                                instance: this.props.instance.id,
                                session: this.props.metadata && this.props.metadata.session && this.props.metadata.session.id ? this.props.metadata.session.id : undefined,
                                pin: this.state.pin,
                                name: this.state.name,
                                phone: this.state.phone,
                                email: this.state.email,
                                store: this.props.store.id,
                                quote: this.props.flow === 'budget-print',
                                document: document,
                                state: this.state.state,
                                city: this.state.city,
                                district: this.state.district,
                                street: this.state.street,
                                status: 'pending',
                                cardType: null,
                                pk: this.props.pk
                            });
                        } else return {status: true};
                    } else throw new Error('save-customer');
                })
                .then((response) => {
                    if (response && response.status) {
                        this.changeState({isSavingCustomer: false, saveCustomerError: false}, () => {
                            if (this.props.allowBudgetPrint) {
                                this.changeState({step: 'budget-preview', isCreatingBudget: true, budgetCreated: false});
                            } else {
                                if (this.props.onRegisterFinished) {
                                    this.props.onRegisterFinished({
                                        client: {
                                            pin: this.state.pin,
                                            entity: this.state.entity,
                                            name: this.state.name,
                                            phone: this.state.phone,
                                            email: this.state.email,
                                            activity: this.state.activity,
                                            activityName: this.getActivityName(this.state.activity),
                                            segment: this.state.segment,
                                            state: this.state.state,
                                            stateName: this.getStateName(this.state.state),
                                            city: this.state.city,
                                            cityName: this.getCityName(this.state.city),
                                            district: this.state.district,
                                            street: this.state.street,
                                        },
                                        company: {
                                            companyName: this.state.companyName,
                                            companyLogo: this.state.budgetLogo,
                                            hqAddress: this.state.hqAddress,
                                            hqActivity: this.state.hqActivity
                                        }
                                    });
                                }
                            }
                        });
                    } else throw new Error('save-order');
                })
                .catch((error) => {
                    Debug.printToLog('error', error);
                    this.changeState({isSavingCustomer: false, saveCustomerError: true});
                });
        });
    };

    onDocumentLoaded = () => {
        setTimeout(() => {
            this.changeState({isCreatingBudget: false, budgetCreated: true});
        }, 2000);
    };

    printBudget = () => {
        Activity.log(this.props.metadata, 'checkout', this.state.step, 'print', true, {type: this.props.flow, entity: this.state.entity, shoppingCart: this.props.shoppingCart.toActivityLog()});
        this.changeState({budgetPrinted: false, budgetPrintError: false}, () => {
            return Printer.printHTMLFromElement(this._containerRef, this._budgetRef)
                .then((status) => {
                    this.changeState({budgetPrinted: true, budgetPrintError: !status});
                })
                .then(() => {
                    setTimeout(() => {
                        this.changeState({budgetPrinted: false, budgetPrintError: false});
                    }, 1000 * 10);
                });
        });
    };

    handleClose = (closeType) => {
        Activity.log(this.props.metadata, 'checkout', closeType, 'close', true, {type: this.props.flow});
        if (this.props.onClose) this.props.onClose();
    };

    handleBack = () => {
        let newStep;
        switch (this.state.step) {
            case 'identification':
                newStep = 'identification';
                break;
            case 'client-entity-selection':
                newStep = 'identification';
                this.clearAll();
                break;
            case 'client-register':
                if (this.state.allowEntityChange) {
                    newStep = 'client-entity-selection';
                    this.clearRegister();
                } else {
                    newStep = 'identification';
                    this.clearAll();
                }
                break;
            case 'budget-preview':
                break;
            default:
                break;
        }
        Activity.log(this.props.metadata, 'checkout', 'back-button', 'back', true, {type: this.props.flow});
        this.changeState({step: newStep});
    };

    clearRegister = () => {
        this.changeState({
            entity: undefined,
            phone: undefined,
            email: undefined,
            activity: undefined,
            state: undefined,
            city: undefined,
            district: undefined,
            street: undefined,
            segment: undefined,
            isCreatingBudget: false,
            budgetCreated: false,
            budgetPrinted: false,
            budgetPrintError: false,
            saveCustomerError: false
        });
    };

    clearAll = () => {
        this.changeState({
            entity: undefined,
            allowEntityChange: true,
            isLoadingEntity: false,
            retries: 0,
            pin: undefined,
            name: undefined,
            allowNameChange: true,
            phone: undefined,
            email: undefined,
            activities: [],
            activity: undefined,
            state: undefined,
            city: undefined,
            district: undefined,
            street: undefined,
            segment: undefined,
            isCreatingBudget: false,
            budgetCreated: false,
            budgetPrinted: false,
            budgetPrintError: false,
            saveCustomerError: false
        });
    };

    renderStep = () => {
        switch (this.state.step) {
            case 'identification':
                return this.renderIdentificationMode();
            case 'client-entity-selection':
                return this.renderEntitySelectionMode();
            case 'client-register':
                return this.renderRegisterMode();
            case 'budget-preview':
                return this.renderPrintMessage();
            default:
                return undefined;
        }
    };

    renderLoader = () => {
        return (
            <div className='signup-loader'>
                <DataLoader message={'Cargando datos de registro...'}/>
            </div>
        );
    };

    renderError = () => {
        return (
            <div className='signup-error'>
                <DataError
                    message='Se ha producido un error al iniciar el flujo de pago'
                    actionLabel='Cerrar'
                    action={() => this.handleClose('signup-error-close-button')}
                    actionButtonLabelColor={this.props.actionButtonLabelColor}
                    actionButtonBackgroundColor={this.props.actionButtonBackgroundColor}
                    actionButtonBorderColor={this.props.actionButtonBorderColor}
                />
            </div>
        )
    };

    renderIdentificationMode = () => {
        return (
            <PaymentIdentification
                isLoading={this.state.isLoadingEntity}
                actionButtonBackgroundColor={this.props.actionButtonBackgroundColor}
                actionButtonBorderColor={this.props.actionButtonBorderColor}
                actionButtonLabelColor={this.props.actionButtonLabelColor}
                cancelButtonBackgroundColor={this.props.cancelButtonBackgroundColor}
                cancelButtonBorderColor={this.props.cancelButtonBorderColor}
                cancelButtonLabelColor={this.props.cancelButtonLabelColor}
                customInputOutline={this.props.customInputOutline}
                customInputClearButtonBackgroundColor={this.props.customInputClearButtonBackgroundColor}
                customInputClearButtonLabelColor={this.props.customInputClearButtonLabelColor}
                onSubmit={this.onIdentificationFinished}
                onClose={undefined}
            />
        );
    };

    renderEntitySelectionMode = () => {
        const options = [
            {
                type: 'person',
                icon: this.props.personOptionImage || defaultPersonOptionImage,
                label1: 'Persona Natural',
                label2: undefined,
                boldLabel1: true,
                boldLabel2: false,
                labelLineBreak: true,
                waitingMode: false,
                waitingModeIcon: undefined,
                waitingModeLabel: undefined,
                disabled: false,
                onClick: this.onEntitySelected
            },
            {
                type: 'company',
                icon: this.props.companyOptionImage || defaultCompanyOptionImage,
                label1: 'Empresa',
                label2: undefined,
                boldLabel1: true,
                boldLabel2: false,
                labelLineBreak: true,
                waitingMode: false,
                waitingModeIcon: undefined,
                waitingModeLabel: undefined,
                disabled: false,
                onClick: this.onEntitySelected
            }
        ];
        return (
            <PaymentOptionSelection
                title1='Nuevo usuario'
                title2='Selecciona una de las siguientes opciones:'
                titleLineBreak={true}
                showShadow={true}
                options={options}
                cancelButtonBackgroundColor={undefined}
                cancelButtonBorderColor={undefined}
                cancelButtonLabelColor={undefined}
                backButtonBackgroundColor={this.props.backButtonBackgroundColor}
                backButtonBorderColor={this.props.backButtonBorderColor}
                backButtonLabelColor={this.props.backButtonLabelColor}
                onClose={undefined}
                onBack={this.handleBack}
            />
        );
    };

    renderRegisterMode = () => {
        return (
            <PaymentRegister
                title1={this.state.entity === 'company' ? 'Empresa' : 'Persona Natural'}
                title2='Ingresa tus datos'
                titleLineBreak={true}
                subtitle='(Todos los campos son obligatorios)'
                entity={this.state.entity || 'person'}
                pin={this.state.pin}
                name={this.state.name}
                allowNameChange={this.state.allowNameChange}
                phone={this.state.phone}
                email={this.state.email}
                states={this.state.states}
                state={this.state.state}
                districts={this.state.districts}
                district={this.state.district}
                cities={this.state.cities}
                city={this.state.city}
                street={this.state.street}
                allActivities={this.state.allActivities}
                activities={this.state.activities}
                activity={this.state.activity}
                segments={this.state.segments}
                segment={this.state.segment}
                isSaving={this.state.isSavingCustomer}
                saveError={this.state.saveCustomerError}
                actionButtonBackgroundColor={this.props.actionButtonBackgroundColor}
                actionButtonBorderColor={this.props.actionButtonBorderColor}
                actionButtonLabelColor={this.props.actionButtonLabelColor}
                cancelButtonBackgroundColor={this.props.cancelButtonBackgroundColor}
                cancelButtonBorderColor={this.props.cancelButtonBorderColor}
                cancelButtonLabelColor={this.props.cancelButtonLabelColor}
                backButtonBackgroundColor={this.props.backButtonBackgroundColor}
                backButtonBorderColor={this.props.backButtonBorderColor}
                backButtonLabelColor={this.props.backButtonLabelColor}
                customInputOutline={this.props.customInputOutline}
                onPinChange={this.handlePinChange}
                onActivityChange={this.handleActivityChange}
                onNameChange={this.handleNameChange}
                onAddressChange={this.handleAddressChange}
                onStateChange={this.handleStateChange}
                onDistrictChange={this.handleDistrictChange}
                onCityChange={this.handleCityChange}
                onPhoneChange={this.handlePhoneChange}
                onEmailChange={this.handleEmailChange}
                onSegmentChange={this.handleSegmentChange}
                onClose={undefined}
                onBack={this.handleBack}
                onSubmit={this.onRegisterFinished}
            />
        )
    };

    renderPrintMessage = () => {
        return (
            <PaymentMessage
                isLoading={this.state.isCreatingBudget}
                loaded={this.state.budgetCreated}
                showPreview={true}
                preview={this.renderDocumentPreview()}
                previewSize={Printer.getDocumentSize()}
                documentUrl={undefined}
                title1={undefined}
                title2='Tu cotización está lista'
                loadingMessage='Preparando cotización...'
                step1Message1='Imprime tu cotizacion'
                step1Message2=' y acércate al mesón de atención para continuar tu compra'
                step1Image={this.props.printMessageImage || defaultPrintMessageImage}
                showStep2={false}
                step2Message1={undefined}
                step2Message2={undefined}
                step2Image={undefined}
                customStepWidth='100%'
                errorMessage='Se ha producido un error al imprimir tu cotización'
                actionLabel='Imprimir'
                actionButtonBackgroundColor={this.props.actionButtonBackgroundColor}
                actionButtonBorderColor={this.props.actionButtonBorderColor}
                actionButtonLabelColor={this.props.actionButtonLabelColor}
                cancelButtonBackgroundColor={undefined}
                cancelButtonBorderColor={undefined}
                cancelButtonLabelColor={undefined}
                onClose={undefined}
                onSubmit={this.printBudget}
                submitted={this.state.budgetPrinted}
                submitSuccess={!this.state.budgetPrintError}
                submitSuccessMessage='Tu cotización se está imprimiendo...'
                submitErrorMessage='Se ha producido un error al imprimir tu cotización'
            />
        );
    };

    renderDocumentPreview = () => {
        return (
            <Budget
                documentRef={this._budgetRef}
                title='PRESUPUESTO ELECTRÓNICO'
                id={this.props.orderNumber}
                idPrefix='N°'
                image={this.state.budgetLogo}
                issuerName={this.state.companyName}
                issuerAddressLabel='Casa Matriz'
                issuerAddress={this.state.hqAddress}
                issuerActivityLabel='Giro'
                issuerActivity={this.state.hqActivity}
                issuerBranchLabel='Sucursal'
                issuerBranch={this.props.store.address}
                receiverName={this.state.name}
                receiverPinLabel='RUT'
                receiverPin={Pin.format(this.state.pin)}
                receiverActivityLabel='Giro'
                receiverActivity={this.getActivityName(this.state.activity)}
                receiverAddressLabel='Dirección'
                receiverAddress={this.state.street}
                receiverDistrictLabel='Comuna'
                receiverDistrict={this.state.district}
                receiverCityLabel='Ciudad'
                receiverCity={this.getCityName(this.state.city)}
                receiverStateLabel='Región'
                receiverState={this.getStateName(this.state.state)}
                additionalInstructions='Lleve este presupuesto a caja para finalizar la venta'
                expirationMessage='Documento válido sólo por 24 horas desde la fecha de emisión'
                shoppingCart={this.props.shoppingCart}
                showTax={true}
                taxMultiplier={this.props.taxMultiplier}
                taxLabel={this.props.taxLabel}
                unitLabel=' c/u'
                onRenderComplete={this.onDocumentLoaded}
            />
        );
    };

    render() {
        return (
            <div ref={this._containerRef} className='signup'>
                {this.isLoadingInitialData() && this.renderLoader()}
                {!this.isLoadingInitialData() && !this.initialDataLoaded() && this.renderError()}
                {!this.isLoadingInitialData() && this.initialDataLoaded() && this.renderStep()}
            </div>
        );
    };
}

Signup.propTypes = {
    flow: PropTypes.string,
    initialData: PropTypes.object,
    instance: PropTypes.object,
    store: PropTypes.object,
    shoppingCart: PropTypes.object,
    orderNumber: PropTypes.string,
    allowBudgetPrint: PropTypes.bool,
    pk: PropTypes.string,
    taxMultiplier: PropTypes.number,
    taxLabel: PropTypes.string,
    mainClientLogo: PropTypes.string,
    personOptionImage: PropTypes.string,
    companyOptionImage: PropTypes.string,
    printMessageImage: PropTypes.string,
    actionButtonBackgroundColor: PropTypes.string,
    actionButtonBorderColor: PropTypes.string,
    actionButtonLabelColor: PropTypes.string,
    cancelButtonBackgroundColor: PropTypes.string,
    cancelButtonBorderColor: PropTypes.string,
    cancelButtonLabelColor: PropTypes.string,
    backButtonBackgroundColor: PropTypes.string,
    backButtonBorderColor: PropTypes.string,
    backButtonLabelColor: PropTypes.string,
    customInputOutline: PropTypes.string,
    customInputClearButtonBackgroundColor: PropTypes.string,
    customInputClearButtonLabelColor: PropTypes.string,
    inSmallMode: PropTypes.bool,
    onClose: PropTypes.func,
    onRegisterFinished: PropTypes.func,
    flowFinished: PropTypes.func,
    metadata: PropTypes.object
};